import React, { memo } from 'react';
import { cn, Icons } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import { UploadRow, UploadStatus } from '../../utils/uploadTypes';

interface FileItemProps {
  row: UploadRow;
  isCurrentFile: boolean;
}

/**
 * D7's consequence: a PURE memoized render of reducer state. Upstream's row holds
 * its own subscription to a per-file uploader and calls .load() from inside the
 * row itself (DicomUploadProgressItem.tsx:31-40); with no PubSub layer that cost
 * does not arise.
 */
const FileItem = memo(
  ({ row, isCurrentFile }: FileItemProps) => {
    const { servicesManager } = useSystem();

    /**
     * §5.5. Obtained from customization, the way upstream's own
     * DicomUploadProgress.tsx:46-47 obtains it — never imported directly. develop
     * imported ProgressLoadingBar from @ohif/ui (FileItem.tsx:2), which 3.13 has
     * moved.
     */
    // customizationService is optional in platform/core/src/types/AppTypes.ts:69, which
    // the fork does not modify; index.tsx:342 established the fix of asserting at the
    // point of use rather than at the declaration.
    const ProgressLoadingBar = (
      servicesManager.services.customizationService as AppTypes.CustomizationService
    ).getCustomization('ui.progressLoadingBar') as React.ComponentType<{ progress?: number }>;

    return (
      <div
        className={cn('mb-1 rounded-lg border px-4 py-2', {
          // D15: border-actions-highlight is platform/ui/tailwind.config.js's literal
          // #5ACCE6, which neither M5.1's token conversion nor develop ever branded.
          // M5.2 plan D-3: one key, not two -- a second 'border-primary-light' entry in this
          // literal would silently discard the first. useDicomUpload's fileSettled sets
          // activeIndex: -1 alongside status: Success, so the two are never both true.
          'border-primary-light': isCurrentFile || row.status === UploadStatus.Success,
          'border-destructive': row.status === UploadStatus.Failed,
          'border-muted-foreground': row.status === UploadStatus.Cancelled,
          'border-input': row.status === UploadStatus.NotStarted,
        })}
        data-cy="sycai-upload-row"
      >
        <div className="mb-1 max-w-[300px]">
          <span
            className="text-foreground block overflow-hidden text-ellipsis whitespace-nowrap text-sm"
            title={row.name}
          >
            {row.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/*
            Plan D-8. 3.13's ProgressLoadingBar takes ONLY `progress`
            (platform/ui-next/src/components/ProgressLoadingBar/ProgressLoadingBar.tsx:6);
            develop passed a className carrying the per-row colour (FileItem.tsx:34-41)
            and it would be silently dropped. The colour lives on the border above,
            which is where develop's own FileItem.tsx:17-22 already put it.
          */}
          <div className="relative flex-1">
            <ProgressLoadingBar progress={row.progress} />
          </div>
          <span className="text-foreground ml-2 min-w-[45px] text-right text-sm">
            {row.progress}%
          </span>
          <div className="group relative h-6 w-6">
            {row.status === UploadStatus.Success && (
              <Icons.StatusSuccess className="text-primary-light" />
            )}
            {row.status === UploadStatus.Cancelled && (
              <Icons.AlertOutline className="text-muted-foreground" />
            )}
            {row.status === UploadStatus.InProgress && (
              <Icons.LoadingSpinner className="text-primary-light animate-spin" />
            )}
            {row.status === UploadStatus.NotStarted && (
              <Icons.Info className="text-muted-foreground" />
            )}
            {row.status === UploadStatus.Failed && (
              <>
                <Icons.StatusError className="text-destructive" />
                {row.error && (
                  <div className="absolute bottom-full right-0 z-50 mb-1 hidden group-hover:block">
                    <div className="w-[250px] rounded bg-black px-2 py-1 text-xs text-white">
                      <div className="whitespace-normal break-all">{row.error}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
  // Fix round 1, #1b. Was a field-by-field compare omitting row.name, which the
  // row renders (:48,:50) -- masked until #1 landed, because before that every
  // row remounted every tick and a memo comparator never ran. react-window's
  // default itemKey is the POST-FILTER position (node_modules/react-window/
  // dist/index.cjs.js:1018), so a field list missing a field can compare two
  // DIFFERENT rows equal when the toggle reshuffles which row sits at a given
  // key. `===` is exact and needs no field list: useDicomUpload's `replaceRow`
  // only ever allocates a new object for the row it patches, so every other row
  // keeps its prior identity and this cannot false-skip.
  (prev, next) => prev.row === next.row && prev.isCurrentFile === next.isCurrentFile
);

FileItem.displayName = 'FileItem';

export default FileItem;
